import React from 'react'
import { Handle, Position } from '@xyflow/react'
import { Answer, Question, QuestionsTypes, QuestionType } from '../../../Types'
import { getBranchColor, NODE_WIDTH } from './graphUtils'
import { COLOR_PRIMARY } from '../../../constants'

import './QuestionNode.css'

import { isBranching } from './dependencyUtils'
import { QuestionHeader } from '../../../components/question-answer/QuestionHeader'
import { Nprs11pt2l } from '../../../components/question-answer/Nprs11pt2lAnswer'
import { AnswerHistory } from '../../../components/question-answer/AnswerHistory'
import { AnswerSubmissionDetails } from '../../../components/question-answer/AnswerSubmissionDetails'

export type QuestionNodeData = {
  question: Question

  defaultLang: string
  index: number
  startQuestionId: string | null
  endQuestionId: string | null
  isWalked: boolean
  answers: Answer[]
}

const handleSize = 12
const MUTED = '#C9C9C9'

const valueQuestions: QuestionType[] = [QuestionsTypes.nprs11pt2l, QuestionsTypes.radio]

export const QuestionNode = ({ data }: { data: QuestionNodeData }) => {
  const { question, defaultLang, isWalked, startQuestionId, endQuestionId, answers } = data

  const isBranchingQuestion: boolean = isBranching(question.type) && !!question.options

  const isFirstQuestion = question._id === startQuestionId
  const isEndQuestion = question._id === endQuestionId

  const isAnswered = answers.length > 0
  const hasAnyAnswers = isAnswered

  const answer = answers[0]
  const isOffline = answer?.offline ? !!answer?.offline : false
  const answerValue = answer?.value

  const isValueQuestion: boolean = valueQuestions.includes(question.type)
  const answerValueIndex: number | boolean = isValueQuestion && parseInt(answerValue, 10)

  const nodeHandleBorder = isWalked ? COLOR_PRIMARY : MUTED

  const options = isBranchingQuestion ? question.options?.map((opt, i) => opt[defaultLang] || `Option ${i + 1}`) : []

  const title = question.title[defaultLang]
  const body = question.body[defaultLang]

  return (
    <div
      className={`container px-0 dep-node bg-white ${isWalked ? 'dep-node--walked' : ''}`}
      style={{ width: NODE_WIDTH }}
    >
      {!isFirstQuestion && (
        <Handle
          type="target"
          style={{
            background: 'white',
            border: `2px solid ${nodeHandleBorder}`,
            width: handleSize,
            height: handleSize,
          }}
          position={Position.Top}
          id="target"
        />
      )}

      <div className="px-3 pt-4 pb-1 " style={{ width: NODE_WIDTH }}>
        <QuestionHeader body={body} title={title} isAnswered={isAnswered} isOffline={isOffline}></QuestionHeader>
        {isBranchingQuestion ? (
          <div className="mt-3 d-flex gap-2">
            {options?.map((label, origIndex) => {
              const isChosen = answerValueIndex === origIndex
              const dimOption = hasAnyAnswers && !isChosen
              const buttonStyle = isChosen
                ? { backgroundColor: COLOR_PRIMARY, color: 'white', borderColor: COLOR_PRIMARY }
                : {
                    backgroundColor: 'white',
                    color: dimOption ? 'rgba(0,0,0,0.45)' : '#333',
                    borderColor: dimOption ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.25)',
                  }
              return (
                <div
                  key={origIndex}
                  className="text-center rounded-2 py-1 px-2"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: '1px solid',
                    fontSize: 11,
                    fontWeight: isChosen ? 600 : 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    ...buttonStyle,
                  }}
                  title={label}
                >
                  {label}
                </div>
              )
            })}
          </div>
        ) : null}
        {question.type === QuestionsTypes.nprs11pt2l ? (
          <div className="mt-3">
            <Nprs11pt2l
              question={question}
              answerIndex={answerValueIndex as number}
              defaultLang={defaultLang}
            ></Nprs11pt2l>
          </div>
        ) : null}
        {question.type === QuestionsTypes.unitcounter ? (
          <>
            {isAnswered && (
              <div className="mt-3 border d-inline-block p-2 rounded-2">
                <h6 className="mb-0">{answer?.value || ''}</h6>
              </div>
            )}
          </>
        ) : null}
        {isBranchingQuestion &&
          options?.map((_, origIndex) => {
            const isChosen = answerValueIndex === origIndex
            const branchColor = isChosen ? getBranchColor(0) : MUTED
            const dimOption = hasAnyAnswers && !isChosen
            const dotColor = dimOption ? MUTED : isChosen ? COLOR_PRIMARY : branchColor
            const lng = options?.length || 1
            const leftPct = ((origIndex + 0.5) / lng) * 100
            return (
              <Handle
                key={`option-handle-${origIndex}`}
                type="source"
                position={Position.Bottom}
                id={`option-${origIndex}`}
                style={{
                  left: `${leftPct}%`,
                  background: dotColor,
                  borderColor: dotColor,
                  width: handleSize,
                  height: handleSize,
                }}
              />
            )
          })}
        {!isEndQuestion && !isBranchingQuestion && (
          <Handle
            type="source"
            style={{
              background: 'white',
              border: `2px solid ${nodeHandleBorder}`,
              right: -8,
              width: handleSize,
              height: handleSize,
            }}
            position={Position.Bottom}
            id="source"
          />
        )}
        {answer && (
          <div className="mt-2 pt-1">
            <AnswerSubmissionDetails
              createdAt={answer?.createdAt}
              deviceTime={answer?.deviceTime}
              isOffline={!!answer?.offline}
              index={0}
              isPrimary={true}
            ></AnswerSubmissionDetails>
          </div>
        )}
        {/*<AnswerHistoryDependency question={question} answers={answers} lang={defaultLang} />*/}
        <div className="mt-2">
          <AnswerHistory answers={answers} question={question} defaultLang={defaultLang}></AnswerHistory>
        </div>
      </div>
    </div>
  )
}
